import { useEffect, useState } from 'react'
import axios from 'axios'
import formHeaderUrl from '/formHeader.png'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000'

interface ActiveEvent {
  id: number
  title: string
  start_date: string
  end_date: string
  description: string
  poster_url: string | null
}

interface RegistrationData {
  full_name: string
  phone: string
  message: string
}

const initialFormData: RegistrationData = {
  full_name: '',
  phone: '',
  message: '',
}

const inputClass =
  'w-full bg-[#1e293b] border border-[#334155] rounded-md px-4 py-3 text-[#f1f5f9] text-sm ' +
  'placeholder-[#64748b] transition-all duration-200 ' +
  'hover:border-[#475569] hover:bg-[#334155] ' +
  'focus:outline-none focus:border-[#3b82f6] focus:bg-[#334155] focus:shadow-[0_0_0_1px_rgba(59,130,246,0.2)]'

const labelClass = 'block text-sm font-medium text-[#94a3b8] mb-2'

function formatDate(dateString: string): string {
  try {
    return new Date(dateString).toLocaleDateString('fr-FR')
  } catch {
    return dateString
  }
}

function formatDateRange(start: string, end: string): string {
  return `${formatDate(start)} - ${formatDate(end)}`
}

export default function App() {
  const [activeEvent, setActiveEvent] = useState<ActiveEvent | null>(null)
  const [loading, setLoading] = useState(true)
  const [formData, setFormData] = useState<RegistrationData>(initialFormData)
  const [isLoading, setIsLoading] = useState(false)
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)
  const [isSubmitted, setIsSubmitted] = useState(false)

  useEffect(() => {
    const fetchActiveEvent = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/events/active`)
        setActiveEvent(response.data.data)
      } catch {
        setActiveEvent(null)
      } finally {
        setLoading(false)
      }
    }
    fetchActiveEvent()
  }, [])

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  const validate = (): string | null => {
    if (!formData.full_name.trim()) {
      return 'Le nom et le prénom sont obligatoires.'
    }
    if (!formData.phone.trim()) {
      return 'Le numéro de téléphone est obligatoire.'
    }
    if (!/^555-\d+$/.test(formData.phone.trim())) {
      return 'Le numéro de téléphone doit être au format 555-XXXX.'
    }
    return null
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setMessage(null)

    const error = validate()
    if (error) {
      setMessage({ type: 'error', text: error })
      return
    }

    setIsLoading(true)

    try {
      await axios.post(`${API_URL}/api/events/register`, formData)
      setIsSubmitted(true)
      setFormData(initialFormData)
    } catch (err) {
      const errorText =
        axios.isAxiosError(err) && err.response?.data?.message
          ? err.response.data.message
          : "Une erreur est survenue lors de l'envoi de l'inscription."
      setMessage({ type: 'error', text: errorText })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-[#0f172a]">
      <div className="w-full max-w-5xl bg-[#1e293b] rounded-xl shadow-2xl border border-[rgba(51,65,85,0.5)] overflow-hidden">
        <div className="w-full h-48 overflow-hidden">
          <img
            src={formHeaderUrl}
            alt="Benny's - Événements"
            className="w-full h-full object-cover"
          />
        </div>

        <div className="p-4 md:p-8">
          {message && (
            <div
              className={`mb-6 p-4 rounded-md text-sm ${
                message.type === 'success'
                  ? 'bg-green-900/30 text-green-400 border border-green-800'
                  : 'bg-red-900/30 text-red-400 border border-red-800'
              }`}
            >
              {message.text}
            </div>
          )}

          {loading ? (
            <p className="text-center text-[#94a3b8]">Chargement...</p>
          ) : isSubmitted ? (
            <div className="text-center py-8">
              <p className="text-green-400 text-lg font-medium">
                Inscription envoyée avec succès. <br />
                Nous vous recontacterons prochainement.
              </p>
            </div>
          ) : activeEvent ? (
            <div className="grid grid-cols-1 md:grid-cols-2">
              <div className="p-4 md:p-8">
                <h2 className="text-[#f1f5f9] text-2xl font-bold mb-2">
                  {activeEvent.title}
                </h2>
                <p className="text-[#94a3b8] text-sm mb-4">
                  {formatDateRange(activeEvent.start_date, activeEvent.end_date)}
                </p>
                <p className="text-[#f1f5f9] text-base whitespace-pre-wrap mb-6">
                  {activeEvent.description}
                </p>
                {activeEvent.poster_url && (
                  <img
                    src={activeEvent.poster_url.trim()}
                    alt="Affiche de l'événement"
                    className="w-full rounded-md object-cover"
                    onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
                  />
                )}
              </div>

              <div className="p-4 md:p-8 md:border-l md:border-[#334155]">
                <h3 className="text-[#f1f5f9] text-lg font-semibold mb-4">
                  Inscription
                </h3>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label htmlFor="full_name" className={labelClass}>
                      Nom et prénom
                    </label>
                    <input
                      type="text"
                      id="full_name"
                      name="full_name"
                      value={formData.full_name}
                      onChange={handleChange}
                      className={inputClass}
                      placeholder="Votre nom et prénom"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className={labelClass}>
                      Téléphone
                    </label>
                    <div className="flex">
                      <span className="inline-flex items-center px-4 py-3 rounded-l-md border border-r-0 border-[#334155] bg-[#334155] text-[#94a3b8] text-sm font-medium">
                        555-
                      </span>
                      <input
                        type="text"
                        id="phone"
                        name="phone"
                        value={formData.phone.startsWith('555-') ? formData.phone.slice(4) : formData.phone}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, '')
                          setFormData((prev) => ({ ...prev, phone: `555-${value}` } as RegistrationData))
                        }}
                        className={`${inputClass} rounded-l-none`}
                        placeholder="1234"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="message" className={labelClass}>
                      Message (optionnel)
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows={4}
                      className={`${inputClass} resize-none`}
                      placeholder="Informations complémentaires..."
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full bg-[#60a5fa] hover:bg-[#3b82f6] text-white font-bold py-3.5 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLoading ? 'Envoi en cours...' : "S'inscrire"}
                  </button>
                </form>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-[#94a3b8] text-lg">
                Aucun événement en cours. <br />
                Revenez plus tard.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
